import { computed, ref, type Ref } from 'vue';
import { useQuery } from '@tanstack/vue-query';

export type ChangelogCategory = 'new' | 'improved' | 'fixed';

export type ChangelogItem = {
  category: ChangelogCategory;
  area?: string;
  headline: string;
  detail?: string;
  /** `repo#number` provenance. Kept for traceability; deliberately not rendered. */
  sources?: string[];
};

export type ChangelogRelease = {
  id: string;
  date: string;
  title?: string;
  items: ChangelogItem[];
};

export type Changelog = {
  schema_version: number;
  generated_at: string | null;
  releases: ChangelogRelease[];
};

/**
 * The changelog is published straight to the S3 bucket this app is served
 * from, so it updates without a deploy. That makes it same-origin static
 * content, NOT an API resource — it deliberately uses `fetch` rather than the
 * `http` axios instance, which would prefix `VITE_API_URL` and attach a bearer
 * token to a public asset.
 *
 * Published by the `changelog` skill; see its reference/schema.md for the
 * contract. In local dev the file does not exist and the fetch 404s, which
 * surfaces as the ordinary empty state.
 */
export const CHANGELOG_URL = '/changelog.json';

const LAST_SEEN_STORAGE_KEY = 'changelog:lastSeenReleaseId';

const EMPTY_CHANGELOG: Changelog = {
  schema_version: 1,
  generated_at: null,
  releases: [],
};

/**
 * A CloudFront SPA fallback answers a missing object with index.html and a 200,
 * so the only way to tell "not published" from "published" is the body itself.
 */
export function looksLikeHtml(body: string): boolean {
  return /^\s*(<!doctype html|<html\b)/i.test(body);
}

const CATEGORY_ORDER: Record<ChangelogCategory, number> = {
  new: 0,
  improved: 1,
  fixed: 2,
};

function isChangelogCategory(value: unknown): value is ChangelogCategory {
  return value === 'new' || value === 'improved' || value === 'fixed';
}

/**
 * The file is written by an external process, so treat it as untrusted input:
 * drop anything malformed rather than letting one bad entry blank the
 * dashboard. Only `id`, `date`, `category` and `headline` are load-bearing.
 */
export function normalizeChangelog(raw: unknown): Changelog {
  const source = (raw ?? {}) as Partial<Changelog>;
  const releases = Array.isArray(source.releases) ? source.releases : [];

  const normalized = releases
    .filter((release): release is ChangelogRelease =>
      Boolean(release && typeof release.id === 'string' && typeof release.date === 'string')
    )
    .map((release) => ({
      id: release.id,
      date: release.date,
      title: typeof release.title === 'string' ? release.title : undefined,
      items: (Array.isArray(release.items) ? release.items : [])
        .filter(
          (item) =>
            Boolean(item) &&
            typeof item.headline === 'string' &&
            item.headline.length > 0 &&
            isChangelogCategory(item.category)
        )
        .sort((first, second) => CATEGORY_ORDER[first.category] - CATEGORY_ORDER[second.category]),
    }))
    .filter((release) => release.items.length > 0);

  return {
    schema_version: typeof source.schema_version === 'number' ? source.schema_version : 1,
    generated_at: typeof source.generated_at === 'string' ? source.generated_at : null,
    releases: normalized,
  };
}

/**
 * Which releases to badge as new. Releases are newest-first, so everything
 * above the last-seen one is unseen.
 *
 * First visit ever (`hasSeenBefore` false) badges nothing — a wall of "New"
 * chips on every release is noise, not news. A last-seen id that isn't in the
 * list anymore (the marker predates the current file) falls back to comparing
 * ids, which sort chronologically by construction — `2026-08-05.2` sorts after
 * `2026-08-05` — rather than badging the whole list.
 */
export function selectNewReleaseIds(
  releases: ChangelogRelease[],
  lastSeenReleaseId: string | null,
  hasSeenBefore: boolean
): Set<string> {
  if (!hasSeenBefore) {
    return new Set<string>();
  }

  const seenIndex = releases.findIndex((release) => release.id === lastSeenReleaseId);
  const unseen =
    seenIndex >= 0
      ? releases.slice(0, seenIndex)
      : releases.filter((release) => release.id > (lastSeenReleaseId ?? ''));

  return new Set(unseen.map((release) => release.id));
}

export type ChangelogGroup = {
  category: ChangelogCategory;
  items: ChangelogItem[];
};

/**
 * Split a release's items into one group per category, in new → improved →
 * fixed order, dropping categories the release has nothing for.
 *
 * The feed renders a group as a bordered box with its category named once in
 * the header, rather than tagging all N rows. That is the whole point: a
 * release of eleven items carried eleven chips of three different widths, so
 * no two headlines started at the same x-position.
 */
export function groupItemsByCategory(items: ChangelogItem[]): ChangelogGroup[] {
  const order: ChangelogCategory[] = ['new', 'improved', 'fixed'];

  return order
    .map((category) => ({
      category,
      items: items.filter((item) => item.category === category),
    }))
    .filter((group) => group.items.length > 0);
}

export type ChangelogMonth = {
  /** `YYYY-MM`, used as the render key. */
  key: string;
  /** The first release in the month, whose date the heading is formatted from. */
  date: string;
  releases: ChangelogRelease[];
};

/**
 * Group consecutive releases under the month they shipped in.
 *
 * The feed puts the month once in a left margin and the specific date once per
 * release, so neither repeats down the page. Releases arrive newest-first and
 * that order is preserved — the file decides ordering, not this function.
 *
 * Grouping is on the `YYYY-MM` prefix rather than a parsed date: `date` is
 * already `YYYY-MM-DD` by contract, and a string slice can't be knocked into
 * the wrong month by a timezone.
 */
export function groupReleasesByMonth(releases: ChangelogRelease[]): ChangelogMonth[] {
  const months: ChangelogMonth[] = [];

  for (const release of releases) {
    const key = release.date.slice(0, 7);
    const current = months[months.length - 1];

    // Only merges into the previous group, never an earlier one — if a file
    // ever interleaves months, that shows up as two headings rather than
    // silently reordering the releases to suit the grouping.
    if (current && current.key === key) {
      current.releases.push(release);
      continue;
    }

    months.push({ key, date: release.date, releases: [release] });
  }

  return months;
}

function readLastSeenReleaseId(): string | null {
  try {
    return window.localStorage.getItem(LAST_SEEN_STORAGE_KEY);
  } catch {
    // Private browsing / disabled storage: the badge is a nicety, not a feature
    // worth breaking the page over.
    return null;
  }
}

function writeLastSeenReleaseId(releaseId: string): void {
  try {
    window.localStorage.setItem(LAST_SEEN_STORAGE_KEY, releaseId);
  } catch {
    // Ignore — see readLastSeenReleaseId.
  }
}

export function useChangelog() {
  const query = useQuery({
    queryKey: ['changelog'],
    queryFn: async (): Promise<Changelog> => {
      const response = await fetch(CHANGELOG_URL, { cache: 'no-cache' });

      // Read the body before deciding anything, because the status alone does
      // not identify "not published yet" — each environment expresses it
      // differently. Straight from the origin it is a 404. Production's
      // CloudFront rewrites it to `200 text/html` via the SPA fallback.
      // Staging's returns `403 text/html`. All three mean the same thing, and
      // an HTML body is what they actually have in common.
      const body = await response.text();

      if (response.status === 404 || looksLikeHtml(body)) {
        return EMPTY_CHANGELOG;
      }

      // A non-HTML error body is a real failure — a gateway error, an outage —
      // and should read as one rather than as an empty changelog.
      if (!response.ok) {
        throw new Error(`Changelog request failed with ${response.status}`);
      }

      // Malformed JSON means a bad publish, which someone needs to see.
      try {
        return normalizeChangelog(JSON.parse(body));
      } catch {
        throw new Error('Changelog response was not valid JSON');
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const releases = computed<ChangelogRelease[]>(() => query.data.value?.releases ?? []);

  // Snapshotted once, before this visit marks anything as seen, so the "New"
  // badges survive re-renders for the whole visit instead of vanishing the
  // moment the data lands.
  const lastSeenReleaseId: Ref<string | null> = ref(readLastSeenReleaseId());
  const hasSeenChangelogBefore = ref(lastSeenReleaseId.value !== null);

  const newReleaseIds = computed<Set<string>>(() =>
    selectNewReleaseIds(releases.value, lastSeenReleaseId.value, hasSeenChangelogBefore.value)
  );

  const newReleaseCount = computed(() => newReleaseIds.value.size);

  /** Call once the list has actually been rendered to the user. */
  function markAllSeen(): void {
    const newest = releases.value[0];
    if (newest) {
      writeLastSeenReleaseId(newest.id);
    }
  }

  return {
    releases,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    newReleaseIds,
    newReleaseCount,
    markAllSeen,
  };
}
